import {
    Alert,
    Box,
    Card,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
} from '@ksyos/design-system';
import { useState } from 'react';
import type { INotificationSettingsValidated } from '@common/validation/serviceOrganizationNotificationSettings';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization } from '@common/enums';
import { serviceOrganizationSettingEmailNotificationEditSchema } from '@common/validation/serviceOrganizationNotificationSettings';
import { translateKey } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils';
import type { IServiceOrganizationSettings } from '../../../interfaces/serviceOrganizationSettings';
import { InputTriState } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { SaveButton } from '../../../userDataManagement/SaveButton';
import { EditForm } from '../OrganizationView';
import { CheckedEmail } from './CheckedEmail';

interface IOrganizationServiceEmailNotificationServiceGroupSettingsFormProps {
    canEdit: boolean;
    editMode: boolean;
    formTitle: string;
    onSaved: () => Promise<void>;
    serviceOrganizationSettings: IServiceOrganizationSettings[];
}

interface INotificationSettings extends IServiceOrganizationSettings {
    isEmailAddressOrganizationInboxAltChecked: boolean;
    isEmailAddressOrganizationInboxChecked: boolean;
}

export function OrganizationServiceEmailNotificationServiceGroupSettingsForm(
    props: IOrganizationServiceEmailNotificationServiceGroupSettingsFormProps
) {
    const abortableFetch = useAbortableFetch();
    const [isEditing, setIsEditing] = useState(props.editMode);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [notificationSettings, setNotificationSettings] = useState<{
        [serviceOrganizationSettingId: string]: INotificationSettings;
    }>(initializeNotificationSettings());

    assertHasAuthorization([ManagerAuthorization.organizationRead]);

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'serviceOrganizationSettings',
                item: notificationSettings,
                errors: errors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        props.canEdit && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={props.formTitle}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {props.serviceOrganizationSettings.map((serviceOrganizationSetting) => (
                            <FormSection
                                key={serviceOrganizationSetting.id}
                                title={serviceOrganizationSetting.service.name}
                            >
                                {!isEditing && renderReadForm(serviceOrganizationSetting)}
                                {isEditing && renderEditForm(serviceOrganizationSetting)}
                            </FormSection>
                        ))}
                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={handleCancel}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    function renderReadForm(serviceOrganizationSetting: IServiceOrganizationSettings) {
        return (
            <>
                <FormGroup label={translateKey('organization.notifications.canOnlyBeReceivedByOrganization')}>
                    <FormParagraph>
                        {translateKey(
                            `common.${serviceOrganizationSetting.sendNotificationOrganizationInbox ? 'yes' : 'no'}`
                        )}
                    </FormParagraph>
                </FormGroup>

                {serviceOrganizationSetting.sendNotificationOrganizationInbox && (
                    <FormGroup label={translateKey('organization.notifications.receiveOn')}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            {!serviceOrganizationSetting.emailAddressOrganizationInbox &&
                                !serviceOrganizationSetting.emailAddressOrganizationInboxAlt &&
                                !serviceOrganizationSetting.organization.emailAddressRequestInbox && (
                                    <FormParagraph>-</FormParagraph>
                                )}

                            {serviceOrganizationSetting.sendNotificationOrganizationEmail &&
                                serviceOrganizationSetting.organization.emailAddressRequestInbox && (
                                    <FormParagraph>
                                        {serviceOrganizationSetting.organization.emailAddressRequestInbox}
                                    </FormParagraph>
                                )}

                            {serviceOrganizationSetting.emailAddressOrganizationInbox && (
                                <FormParagraph>
                                    {serviceOrganizationSetting.emailAddressOrganizationInbox}
                                </FormParagraph>
                            )}

                            {serviceOrganizationSetting.emailAddressOrganizationInboxAlt && (
                                <FormParagraph>
                                    {serviceOrganizationSetting.emailAddressOrganizationInboxAlt}
                                </FormParagraph>
                            )}
                        </Box>
                    </FormGroup>
                )}
            </>
        );
    }

    function renderEditForm(serviceOrganizationSetting: IServiceOrganizationSettings) {
        const notificationSetting = notificationSettings[serviceOrganizationSetting.id];

        return (
            <>
                {!serviceOrganizationSetting.organization.emailAddressRequestInbox && (
                    <Alert
                        appearance={
                            hasEmailError(serviceOrganizationSetting, ['emailAddressRequestInbox', 'email_required'])
                                ? 'warning'
                                : 'info'
                        }
                    >
                        {translateKey('organization.notifications.organizationEmailNotSet')}&nbsp;
                        <Alert.Link
                            to={`/organizations/${serviceOrganizationSetting.organization.id}/view/details?editForm=${EditForm.generalSettings}&focusField=emailAddressRequestInbox`}
                            variant="link"
                        >
                            {translateKey('organization.notifications.setOrganizationEmail')}
                        </Alert.Link>
                    </Alert>
                )}

                {hasEmailError(serviceOrganizationSetting, ['email_required']) &&
                    notificationSetting.sendNotificationOrganizationInbox && (
                        <Alert appearance="warning">{translateKey('carePath.noEmailSelected')}</Alert>
                    )}

                <FormGroup label={translateKey('organization.notifications.canOnlyBeReceivedByOrganization')}>
                    <InputTriState
                        error={getValidationErrorTranslation(
                            errors.get(`${serviceOrganizationSetting.id}.sendNotificationOrganizationInbox`)
                        )}
                        fieldKey={`${serviceOrganizationSetting.id}`}
                        name={`${serviceOrganizationSetting.id}.sendNotificationOrganizationInbox`}
                        notNullable
                        onChange={async (value) =>
                            handleChange(
                                [serviceOrganizationSetting.id, 'sendNotificationOrganizationInbox'],
                                Boolean(value)
                            )
                        }
                        value={notificationSetting.sendNotificationOrganizationInbox}
                    />
                </FormGroup>

                {notificationSetting.sendNotificationOrganizationInbox && (
                    <FormGroup label={translateKey('personalSettings.notifications.receiveOn')}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            {serviceOrganizationSetting.organization.emailAddressRequestInbox && (
                                <CheckedEmail
                                    email={serviceOrganizationSetting.organization.emailAddressRequestInbox}
                                    isChecked={Boolean(notificationSetting.sendNotificationOrganizationEmail)}
                                    onChange={async (isChecked) =>
                                        handleChange(
                                            [serviceOrganizationSetting.id, 'sendNotificationOrganizationEmail'],
                                            isChecked
                                        )
                                    }
                                    readOnly
                                />
                            )}

                            <CheckedEmail
                                email={notificationSetting.emailAddressOrganizationInbox}
                                error={getValidationErrorTranslation(
                                    errors.get(`${serviceOrganizationSetting.id}.emailAddressOrganizationInbox`)
                                )}
                                isChecked={notificationSetting.isEmailAddressOrganizationInboxChecked}
                                onBlur={async () =>
                                    validateOne(`${serviceOrganizationSetting.id}.emailAddressOrganizationInbox`)
                                }
                                onChange={async (isChecked, email) =>
                                    handleEmailCheckedChange(
                                        'isEmailAddressOrganizationInboxChecked',
                                        'emailAddressOrganizationInbox',
                                        isChecked,
                                        email,
                                        serviceOrganizationSetting.id
                                    )
                                }
                            />

                            <CheckedEmail
                                email={notificationSetting.emailAddressOrganizationInboxAlt}
                                error={getValidationErrorTranslation(
                                    errors.get(`${serviceOrganizationSetting.id}.emailAddressOrganizationInboxAlt`)
                                )}
                                isChecked={notificationSetting.isEmailAddressOrganizationInboxAltChecked}
                                onBlur={async () =>
                                    validateOne(`${serviceOrganizationSetting.id}.'emailAddressOrganizationInboxAlt'`)
                                }
                                onChange={async (isChecked, email) =>
                                    handleEmailCheckedChange(
                                        'isEmailAddressOrganizationInboxAltChecked',
                                        'emailAddressOrganizationInboxAlt',
                                        isChecked,
                                        email,
                                        serviceOrganizationSetting.id
                                    )
                                }
                            />
                        </Box>
                    </FormGroup>
                )}
            </>
        );
    }

    function initializeNotificationSettings() {
        return props.serviceOrganizationSettings.reduce(
            (notifications, serviceOrganizationSetting) => {
                notifications[serviceOrganizationSetting.id] = {
                    ...serviceOrganizationSetting,
                    isEmailAddressOrganizationInboxChecked: Boolean(
                        serviceOrganizationSetting.emailAddressOrganizationInbox
                    ),
                    isEmailAddressOrganizationInboxAltChecked: Boolean(
                        serviceOrganizationSetting.emailAddressOrganizationInboxAlt
                    ),
                };

                return notifications;
            },
            {} as { [key: string]: INotificationSettings }
        );
    }

    function hasEmailError(serviceOrganizationSetting: IServiceOrganizationSettings, errorArray: string[]) {
        const requestInboxErrors = errors
            .entrySeq()
            .toArray()
            .filter((error) => {
                const [serviceOrganizationSettingId, name] = error;

                return (
                    serviceOrganizationSettingId === `${serviceOrganizationSetting.id}.` && errorArray.includes(name)
                );
            });

        return requestInboxErrors.length > 0;
    }

    function handleEmailCheckedChange<
        TCheckedKey extends keyof Pick<
            INotificationSettings,
            'isEmailAddressOrganizationInboxChecked' | 'isEmailAddressOrganizationInboxAltChecked'
        >,
        TEmailKey extends keyof Pick<
            INotificationSettings,
            'emailAddressOrganizationInbox' | 'emailAddressOrganizationInboxAlt'
        >,
    >(
        checkedKey: TCheckedKey,
        emailKey: TEmailKey,
        checked: boolean,
        email: string | null | undefined,
        serviceOrganizationSettingId: string
    ) {
        if (notificationSettings[serviceOrganizationSettingId][checkedKey] !== checked) {
            // Remove any errors when checked changed, since we can't use onblur..
            setErrors(errors.remove(`${serviceOrganizationSettingId}.${emailKey}`));
        }

        setNotificationSettings({
            ...notificationSettings,
            [serviceOrganizationSettingId]: {
                ...notificationSettings[serviceOrganizationSettingId],
                [checkedKey]: checked,
                [emailKey]: email,
            },
        });
    }

    function handleChange<K extends keyof INotificationSettings>(name: [string, K], value: INotificationSettings[K]) {
        const serviceOrganizationSettingId = name[0];
        const key = name[1];
        setNotificationSettings({
            ...notificationSettings,
            [serviceOrganizationSettingId]: {
                ...notificationSettings[serviceOrganizationSettingId],
                [key]: value,
            },
        });
    }

    function validateOne(name: string) {
        setErrors(
            updateValidationErrorsForOne(
                serviceOrganizationSettingEmailNotificationEditSchema,
                notificationSettings,
                errors,
                name
            )
        );
    }

    async function handleCancel() {
        setNotificationSettings(initializeNotificationSettings());
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (!notificationSettings) {
            return;
        }

        setIsSaving(true);

        const updatedServiceOrganizationSettings: [string, INotificationSettingsValidated][] = [];
        let errorsForAllServiceOrganizationSettings = emptyValidationErrors;

        for (const serviceOrganizationSettingId of Object.keys(notificationSettings)) {
            const validatedNotificationSetting = validate(
                serviceOrganizationSettingEmailNotificationEditSchema,
                notificationSettings[serviceOrganizationSettingId]
            );

            const organizationEmailAddressRequestInbox = props.serviceOrganizationSettings?.find(
                (serviceOrganizationSetting) => serviceOrganizationSetting.id === serviceOrganizationSettingId
            )?.organization.emailAddressRequestInbox;
            if (
                notificationSettings[serviceOrganizationSettingId].sendNotificationOrganizationInbox &&
                !organizationEmailAddressRequestInbox &&
                !notificationSettings[serviceOrganizationSettingId].emailAddressOrganizationInbox &&
                !notificationSettings[serviceOrganizationSettingId].emailAddressOrganizationInboxAlt
            ) {
                validatedNotificationSetting.errors = validatedNotificationSetting.errors.set(
                    `${serviceOrganizationSettingId}`,
                    'email_required'
                );
            }

            if (validatedNotificationSetting.errors.size > 0) {
                validatedNotificationSetting.errors
                    .entrySeq()
                    .toArray()
                    .forEach((entry) => {
                        errorsForAllServiceOrganizationSettings = errorsForAllServiceOrganizationSettings.set(
                            `${serviceOrganizationSettingId}`,
                            `${entry[1]}`
                        );
                    });
            }

            if (validatedNotificationSetting.item) {
                updatedServiceOrganizationSettings.push([
                    serviceOrganizationSettingId,
                    validatedNotificationSetting.item,
                ]);
            }
        }

        if (errorsForAllServiceOrganizationSettings.size > 0) {
            setErrors(errorsForAllServiceOrganizationSettings);
            setIsSaving(false);
        } else {
            for (const updatedServiceOrganizationSetting of updatedServiceOrganizationSettings) {
                const [updatedServiceOrganizationSettingId, validateServiceOrganizationSetting] =
                    updatedServiceOrganizationSetting;
                const url = `/api/serviceOrganizationSettings/${updatedServiceOrganizationSettingId}/notificationSettings`;
                await abortableFetch.postJson(url, validateServiceOrganizationSetting);
            }
            await props.onSaved();
            setErrors(emptyValidationErrors);
            setIsEditing(false);
            setIsSaving(false);
        }
    }
}
